import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ExternalLink, RefreshCw, Loader2, ImageOff } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND}${u}`);

/**
 * Admin verification queue — one row per pending booking.
 * Shows license + insurance photos side-by-side with approve / reject actions.
 */
export default function VerifyQueue() {
  const [rows, setRows] = useState(null);
  const [notes, setNotes] = useState({}); // { booking_id: text }
  const [busy, setBusy] = useState(null); // booking_id being acted on

  const load = () => api.get("/admin/verification-queue")
    .then((r) => setRows(r.data))
    .catch(() => setRows([]));

  useEffect(() => { load(); }, []);

  const act = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/admin/bookings/${id}/verify`, { status, notes: notes[id] || null });
      toast.success(status === "approved" ? "Approved — customer notified" : "Rejected — customer notified");
      setNotes((n) => ({ ...n, [id]: "" }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  if (rows === null) {
    return (
      <div className="card-bl p-6 flex items-center gap-3 text-[#AEB9C9]" data-testid="verify-loading">
        <Loader2 size={16} className="animate-spin" /> Loading verification queue…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="verify-queue">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-heading font-semibold text-white">Verification queue</h2>
          <p className="text-xs text-[#AEB9C9]">Review the driver's license + insurance card photos before pickup. Approving emails the customer their green-light.</p>
        </div>
        <Button onClick={load} className="bg-white/5 hover:bg-white/10 text-white border border-white/20" data-testid="verify-refresh">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="card-bl p-10 text-center text-[#AEB9C9]" data-testid="verify-empty">
          <ShieldCheck size={28} className="mx-auto opacity-50" />
          <div className="mt-3 text-sm">Nothing pending — every booking is verified. Nice work.</div>
        </div>
      ) : rows.map((b) => {
        const v = b.verification || {};
        const missing = !v.license_image_url && !v.insurance_image_url;
        return (
          <div key={b.booking_id} className="card-bl p-5" data-testid={`verify-row-${b.booking_id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="font-heading font-semibold text-white">
                  {b.vehicle_name} · {b.start_date} → {b.end_date}
                </div>
                <div className="text-xs text-[#AEB9C9] mt-1">
                  {b.guest_name} · {b.guest_email} · {b.guest_phone} · Booking {b.booking_id}
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#E2AB20]">
                <ShieldAlert size={12} /> {v.status || "pending"}
              </span>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {/* License */}
              <div>
                <div className="text-xs uppercase tracking-widest text-[#AEB9C9] mb-2">Driver license</div>
                <div className="text-xs text-white">
                  {v.license_number} · {v.license_state} · exp {v.license_expiry}
                </div>
                {v.license_image_url ? (
                  <a href={absUrl(v.license_image_url)} target="_blank" rel="noreferrer" className="mt-2 block">
                    <img
                      src={absUrl(v.license_image_url)}
                      alt="License"
                      className="w-full max-h-60 object-contain rounded-lg border border-white/10 bg-black/30"
                      data-testid={`verify-license-img-${b.booking_id}`}
                    />
                    <div className="mt-1 text-[11px] text-[#E2AB20] inline-flex items-center gap-1"><ExternalLink size={10} /> Open full size</div>
                  </a>
                ) : (
                  <div className="mt-2 h-32 rounded-lg border border-dashed border-white/10 grid place-items-center text-[#AEB9C9] text-xs"><ImageOff size={14} className="mr-2" /> No license photo uploaded</div>
                )}
              </div>
              {/* Insurance */}
              <div>
                <div className="text-xs uppercase tracking-widest text-[#AEB9C9] mb-2">Insurance</div>
                <div className="text-xs text-white">
                  {v.insurance_provider} · {v.insurance_policy} · exp {v.insurance_expiry}
                </div>
                {v.insurance_image_url ? (
                  <a href={absUrl(v.insurance_image_url)} target="_blank" rel="noreferrer" className="mt-2 block">
                    <img
                      src={absUrl(v.insurance_image_url)}
                      alt="Insurance"
                      className="w-full max-h-60 object-contain rounded-lg border border-white/10 bg-black/30"
                      data-testid={`verify-insurance-img-${b.booking_id}`}
                    />
                    <div className="mt-1 text-[11px] text-[#E2AB20] inline-flex items-center gap-1"><ExternalLink size={10} /> Open full size</div>
                  </a>
                ) : (
                  <div className="mt-2 h-32 rounded-lg border border-dashed border-white/10 grid place-items-center text-[#AEB9C9] text-xs"><ImageOff size={14} className="mr-2" /> No insurance photo uploaded</div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <textarea
                placeholder="Optional note to customer (e.g. 'Photo is blurry — please resend')"
                value={notes[b.booking_id] || ""}
                onChange={(e) => setNotes({ ...notes, [b.booking_id]: e.target.value })}
                className="w-full text-sm bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white placeholder-[#8896AC] focus:outline-none focus:border-[#E2AB20]"
                rows={2}
                data-testid={`verify-notes-${b.booking_id}`}
              />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => act(b.booking_id, "approved")}
                  disabled={busy === b.booking_id || missing}
                  className="bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold disabled:opacity-50"
                  data-testid={`verify-approve-${b.booking_id}`}
                >
                  {busy === b.booking_id ? "…" : "Approve"}
                </Button>
                <Button
                  onClick={() => act(b.booking_id, "rejected")}
                  disabled={busy === b.booking_id}
                  className="bg-red-500/80 hover:bg-red-500 text-white disabled:opacity-50"
                  data-testid={`verify-reject-${b.booking_id}`}
                >
                  {busy === b.booking_id ? "…" : "Reject"}
                </Button>
                {missing && (
                  <span className="text-xs text-[#AEB9C9]">Customer hasn't uploaded photos yet — approve disabled.</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
